import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Generic admin CRUD page.
 * Fields: [{ name, label, type, placeholder, required, options }]
 * type: text, textarea, number, select, url, checkbox, array-text (comma-separated)
 */
export default function AdminCrudPage({ title, endpoint, fields, testIdPrefix, columns }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get(`/admin/${endpoint}`).then(({ data }) => setItems(data));
  useEffect(() => { load(); }, [endpoint]);

  const openNew = () => {
    const blank = {};
    fields.forEach((f) => {
      blank[f.name] = f.type === "checkbox" ? (f.default ?? false)
        : f.type === "number" ? (f.default ?? 0)
        : f.type === "array-text" ? "" : (f.default ?? "");
    });
    setEditing(blank);
    setShowForm(true);
  };

  const openEdit = (item) => {
    const copy = { ...item };
    fields.forEach((f) => {
      if (f.type === "array-text" && Array.isArray(copy[f.name])) copy[f.name] = copy[f.name].join(", ");
      if (f.type === "json") copy[f.name] = JSON.stringify(copy[f.name] ?? [], null, 2);
    });
    setEditing(copy);
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...editing };
    for (const f of fields) {
      if (f.type === "array-text") payload[f.name] = (payload[f.name] || "").split(",").map((s) => s.trim()).filter(Boolean);
      if (f.type === "number") payload[f.name] = Number(payload[f.name] || 0);
      if (f.type === "json") {
        try { payload[f.name] = payload[f.name] ? JSON.parse(payload[f.name]) : []; }
        catch { toast.error(`Invalid JSON in ${f.label}`); return; }
      }
    }
    try {
      if (editing.id) await api.patch(`/admin/${endpoint}/${editing.id}`, payload);
      else await api.post(`/admin/${endpoint}`, payload);
      toast.success("Saved");
      setShowForm(false); setEditing(null); load();
    } catch (e) { toast.error("Save failed: " + (e.response?.data?.detail || "error")); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this item?")) return;
    await api.delete(`/admin/${endpoint}/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-black text-3xl" data-testid={`${testIdPrefix}-title`}>{title}</h1>
        <button onClick={openNew} className="btn-primary-eq text-sm" data-testid={`${testIdPrefix}-add`}><Plus className="w-4 h-4" /> Add New</button>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-500 text-xs uppercase tracking-wider">
            <tr>
              {columns.map((c) => <th key={c.key} className="text-left px-4 py-3">{c.label}</th>)}
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3">
                    {c.render ? c.render(it) : (Array.isArray(it[c.key]) ? it[c.key].join(", ") : String(it[c.key] ?? ""))}
                  </td>
                ))}
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={() => openEdit(it)} className="p-1.5 hover:bg-neutral-100 rounded" data-testid={`${testIdPrefix}-edit-${it.id}`}><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => remove(it.id)} className="p-1.5 hover:bg-red-100 text-red-600 rounded" data-testid={`${testIdPrefix}-delete-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={columns.length + 1} className="text-center py-16 text-neutral-500">No items yet</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={save} className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid={`${testIdPrefix}-form`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-xl">{editing.id ? "Edit" : "New"} {title.replace(/s$/, "")}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="grid gap-4">
              {fields.map((f) => (
                <div key={f.name}>
                  <label className="text-xs font-semibold text-neutral-600">{f.label}{f.required && " *"}</label>
                  {f.type === "textarea" && (
                    <textarea required={f.required} rows={f.rows || 4} value={editing[f.name] || ""} onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid={`${testIdPrefix}-field-${f.name}`} />
                  )}
                  {f.type === "select" && (
                    <select value={editing[f.name] || ""} onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm">
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  )}
                  {f.type === "checkbox" && (
                    <label className="flex items-center gap-2 mt-1">
                      <input type="checkbox" checked={!!editing[f.name]} onChange={(e) => setEditing({ ...editing, [f.name]: e.target.checked })} />
                      <span className="text-sm">{f.checkboxLabel || f.label}</span>
                    </label>
                  )}
                  {["text", "number", "url", "array-text"].includes(f.type) && (
                    <input required={f.required} type={f.type === "number" ? "number" : "text"}
                      placeholder={f.placeholder} value={editing[f.name] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      data-testid={`${testIdPrefix}-field-${f.name}`} />
                  )}
                  {f.type === "json" && (
                    <textarea rows={f.rows || 10}
                      placeholder={f.placeholder || "[]"}
                      value={typeof editing[f.name] === "string" ? editing[f.name] : JSON.stringify(editing[f.name] ?? [], null, 2)}
                      onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg font-mono text-xs"
                      data-testid={`${testIdPrefix}-field-${f.name}`} />
                  )}
                </div>
              ))}
            </div>
            <button type="submit" className="btn-primary-eq w-full justify-center mt-6" data-testid={`${testIdPrefix}-save`}>Save</button>
          </form>
        </div>
      )}
    </div>
  );
}
